/**
 * Source-backed execution inventory and the generic job-queue gate.
 *
 * The taxonomy (`NODE_SUB_PURPOSE_MAP`) and the explicit non-operation list
 * (`WEB_NODES_WITHOUT_OPERATION`) decide what may enter `POST /api/jobs/create`.
 * `webNodeOperation` is a Studio planning map, not this decision: a missing
 * planning entry can still be a hosted image or video model, so it is not a
 * blanket block. Queue reachability is recorded separately from a verified
 * end-to-end capability claim.
 *
 * Web sound and web 3D stay unavailable. Desktop hosted jobs that do not
 * declare a canvas family are not that web path.
 */
import { NODE_SUB_PURPOSE_MAP } from '../pools/ai/model-sub-purpose.schema';
import { WEB_NODE_TYPES, WEB_NODES_WITHOUT_OPERATION } from './web-operations';

export const NO_EXECUTABLE_ADAPTER = 'NO_EXECUTABLE_ADAPTER';

export type ExecutionRoute = 'queued' | 'direct' | 'local' | 'unavailable';

export interface ExecutionInventoryEntry {
  key: string;
  nodeType: string;
  purpose: string;
  subPurpose?: string;
  route: ExecutionRoute;
  explanation: string;
}

/** Canvas families that are inputs, editors, or workflows — never a generic generation. */
export const LOCAL_CANVAS_FAMILIES = [
  'FrameInput',
  'FrameOutput',
  'TextFile',
  'ImageFile',
  'VideoFile',
  'AudioFile',
  'CommentNode',
  'MaskNode',
  'DataTable',
  'VariationGenerator',
  'PromptComposer',
  'BatchGenerator',
  'GalleryGroup',
  'GalleryContainer',
  'LoraModel',
  'ShaderNode',
  'shader',
] as const;

const LOCAL_FAMILY = new Set<string>(LOCAL_CANVAS_FAMILIES);

const PURPOSES = ['create', 'edit', 'enhance', 'tools'] as const;

const UNAVAILABLE_EXPLANATION: Record<'sound' | '3d', string> = {
  sound: 'Web sound has no verified request schema, queue type, adapter, persisted result, and canvas output, so it does not enter the generic job queue.',
  '3d': 'Web 3D has no verified request schema, queue type, adapter, persisted result, and canvas renderer, so it does not enter the generic job queue.',
};

const LOCAL_EXPLANATION =
  'This is an input, editor, or workflow on the canvas, not a standalone generation. It does not enter the generic job queue.';

/**
 * Structural non-operations are inputs, editors, utilities, or jobs that outlive
 * a graph run. A selected catalogue model does not turn them into a generic
 * generation. "No operation offers it yet" is a capability gap, not this class:
 * a hosted model selected for that tool may still use the generic image or
 * video queue.
 */
export function isStructuralNonOperation(explanation: string): boolean {
  const text = explanation.toLowerCase();
  return (
    text.includes('not an operation')
    || text.includes('its own editor')
    || text.includes('outlives a graph run')
    || text.includes('not a model run')
    || text.includes('display of a clip')
  );
}

function normalizeFamily(nodeType: string | undefined): string | undefined {
  if (!nodeType) return undefined;
  const trimmed = nodeType.trim();
  if (!trimmed) return undefined;
  if (Object.hasOwn(WEB_NODE_TYPES, trimmed)) return WEB_NODE_TYPES[trimmed];
  if (trimmed === 'text') return 'text-llm';
  return trimmed;
}

function routeFor(nodeType: string, purpose: string, subPurpose?: string): Pick<ExecutionInventoryEntry, 'route' | 'explanation'> {
  if (nodeType === 'sound' || nodeType === '3d') {
    return { route: 'unavailable', explanation: UNAVAILABLE_EXPLANATION[nodeType] };
  }
  if (nodeType === 'text-llm') {
    return {
      route: 'direct',
      explanation: 'Text streams through the AI API. It is not a generic job-queue operation.',
    };
  }
  if (subPurpose) {
    const key = `${nodeType}/${purpose}/${subPurpose}`;
    const excused = WEB_NODES_WITHOUT_OPERATION[key];
    if (excused && isStructuralNonOperation(excused)) {
      return { route: 'local', explanation: excused };
    }
    if (excused) {
      return {
        route: 'queued',
        explanation: `${excused} A selected hosted model may still use the generic queue; that is not a verified typed adapter.`,
      };
    }
  }
  return {
    route: 'queued',
    explanation: 'Generic image or video queue. Reachability is not a claim that every visible setting hits a typed adapter.',
  };
}

/** Every taxonomy combination, classified from the schema sources above. */
export function executionInventory(): ExecutionInventoryEntry[] {
  const entries: ExecutionInventoryEntry[] = [];
  for (const [nodeType, purposes] of Object.entries(NODE_SUB_PURPOSE_MAP)) {
    for (const purpose of PURPOSES) {
      const subs = (purposes as Record<string, readonly string[]>)[purpose] ?? [];
      if (subs.length === 0) {
        const classified = routeFor(nodeType, purpose);
        entries.push({
          key: `${nodeType}/${purpose}`,
          nodeType,
          purpose,
          route: classified.route,
          explanation: classified.explanation,
        });
      }
      for (const subPurpose of subs) {
        const classified = routeFor(nodeType, purpose, subPurpose);
        entries.push({
          key: `${nodeType}/${purpose}/${subPurpose}`,
          nodeType,
          purpose,
          subPurpose,
          route: classified.route,
          explanation: classified.explanation,
        });
      }
    }
  }
  return entries;
}

export interface JobAdmissionInput {
  jobType: string;
  nodeType?: string;
  purpose?: string;
  subPurpose?: string;
  sourceFamily?: string;
  modelId?: string;
}

export type JobAdmission =
  | { admit: true; route: 'queued' | 'direct' | 'hosted' }
  | { admit: false; code: typeof NO_EXECUTABLE_ADAPTER; explanation: string };

function refuse(explanation: string): JobAdmission {
  return { admit: false, code: NO_EXECUTABLE_ADAPTER, explanation };
}

/**
 * Backend-owned decision for one model/tool combination.
 * Refused combinations must not be charged or enqueued.
 */
export function decideGenericJobAdmission(input: JobAdmissionInput): JobAdmission {
  const family = normalizeFamily(input.sourceFamily ?? input.nodeType);
  const purpose = input.purpose?.trim() || undefined;
  const subPurpose = input.subPurpose?.trim() || undefined;
  const modelId = input.modelId?.trim() || undefined;

  if (family && LOCAL_FAMILY.has(family)) return refuse(LOCAL_EXPLANATION);
  if (family === 'sound' || family === '3d') return refuse(UNAVAILABLE_EXPLANATION[family]);

  if (family === 'text-llm') {
    if (input.jobType === 'TEXT_TO_IMAGE' || input.jobType === 'IMAGE_TO_IMAGE' || input.jobType === 'IMAGE_UPSCALE' || input.jobType === 'VIDEO_GENERATION') {
      return refuse('Text streams through the AI API. It does not enter the generic image or video queue.');
    }
    return { admit: true, route: 'direct' };
  }

  if (family === 'image' || family === 'video') {
    if (purpose && subPurpose) {
      const key = `${family}/${purpose}/${subPurpose}`;
      const excused = WEB_NODES_WITHOUT_OPERATION[key];
      if (excused && isStructuralNonOperation(excused)) return refuse(excused);
      if (excused && !modelId) return refuse(excused);
    }
    return { admit: true, route: 'queued' };
  }

  if (!family) return { admit: true, route: 'hosted' };

  return refuse(`No executable adapter is registered for ${family}.`);
}
